
import api from "@/api";
import { Device } from "@/api/dtos/Device.schema";
import { DevicePropertyDescription } from "@/api/dtos/DevicePropertyDescription.schema";
import { DeviceTypeDescription } from "@/api/dtos/DeviceTypeDescription.schema";
import { DeviceTypeSettings } from "@/api/dtos/DeviceTypeSettings.schema";
import { OperatorSummary } from "@/api/dtos/OperatorSummary.schema";
import { Pricing } from "@/api/dtos/Pricing.schema";
import { RentalMethod } from "@/api/dtos/RentalMethod.schema";
import { DeviceDimensions, Station } from "@/api/dtos/Station.schema";
import { StationLocation } from "@/api/dtos/StationLocation.schema";
import { StationSupportContacts } from "@/api/dtos/StationSupportContacts.schema";
import ContainerFrame from "@/components/ContainerFrame.vue";
import BlockAndDeleteStation from "@/components/stationDetails/BlockAndDeleteStation.vue";
import DeviceSettings from "@/components/stationDetails/DeviceSettings.vue";
import CardsOverview from "@/components/stationDetails/CardsOverview.vue";
import EditFrame from "@/components/EditFrame.vue";
import FirmwareUpdateForm from "@/components/stationDetails/FirmwareUpdateForm.vue";
import OperatorChooser from "@/components/stationDetails/OperatorChooser.vue";
import PropertyDescriptions from "@/components/stationDetails/PropertyDescriptions.vue";
import UpdateableImage from "@/components/operator/UpdateableImage.vue";
import StationLocationForm from "@/components/stationDetails/StationLocationForm.vue";
import StationPricing from "@/components/stationDetails/StationPricing.vue";
import StationTimeZone from "@/components/stationDetails/StationTimeZone.vue";
import StationSupportContactsForm from "@/components/stationDetails/StationSupportContactsForm.vue";
import TypeDescriptions from "@/components/stationDetails/TypeDescriptions.vue";
import StationPlatformService from "@/components/stationDetails/StationPlatformService.vue";
import { OperatorsModule } from "@/store/modules/OperatorsModule";
import { StationsModule } from "@/store/modules/StationsModule";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import CancelButton from "@/components/buttons/CancelButton.vue";
import AddDeviceUserDialog from "@/components/dialogs/AddDeviceUserDialog.vue";
import SectionNav, { Section } from "@/components/SectionNav.vue";

import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";
import { UsersModule } from "@/store/modules/UsersModule";
import { User } from "@/api/dtos/User.schema";
import { DeviceHardwareSettings } from "@/api/dtos/DeviceHardwareSettings.schema";

import * as firmwareApi from "@/api/firmwares";
import type { FirmwareVersion } from "@/api/dtos/FirmwareVersion.schema";
import type { Firmware } from "@/api/dtos/Firmware.schema";

@Component({
  components: {
    CancelButton,
    ContainerFrame,
    StationLocationForm,
    StationPricing,
    StationTimeZone,
    DeviceSettings,
    TypeDescriptions,
    PropertyDescriptions,
    CardsOverview,
    StationSupportContactsForm,
    FirmwareUpdateForm,
    OperatorChooser,
    BlockAndDeleteStation,
    StationPlatformService,
    UpdateableImage,
    EditFrame,
    AddDeviceUserDialog,
    SectionNav,
  },
})
export default class StationDetailsView extends Vue {
  mounted(): void {
    StationsModule.fetchDetailedStation(this.$route.params.stationUuid);
    OperatorsModule.fetchOperatorSummaries();
    this.fetchImage();
    this.fetchFirmwareVersions();
  }

  sectionList: Section[] = [
    {
      id: "upload-image",
      label: "Stationsbild",
    },
    {
      id: "location",
      label: "Standort",
    },
    {
      id: "pricing",
      label: "Preise",
    },
    {
      id: "time-zone",
      label: "Zeitzone",
    },
    {
      id: "device-settings",
      label: "Boxeinstellungen",
    },
    {
      id: "descriptions",
      label: "Beschreibungen ",
    },
    // {
    //   id: "card",
    //   label: "Karten",
    // },
    {
      id: "support",
      label: "Support",
    },
    // {
    //   id: "firmware-update",
    //   label: "Firmware-Update",
    // },
    {
      id: "operator-selection",
      label: "Verwaltung & Einstellungen",
    },
  ];

  stationImage: File | null = null;
  firmwares: Firmware[] = [];

  selectedFirmwareVersionId: number | null = null;

  get firmwareVersionOptions(): FirmwareVersionOption[] {
    return this.firmwares.flatMap(fw =>
      (fw.versions || []).map(v => ({
        id: v.id,
        firmwareId: fw.id as number,
        label: `${fw.name} - ${v.version}`,
        version: v.version,
      }))
    );
  }

  get station(): Station | null {
    return StationsModule.detailedStation;
  }

  get operators(): OperatorSummary[] {
    return OperatorsModule.operatorSummaries;
  }

  get localUser(): User | null {
    return UsersModule.localUser;
  }

  get isAdmin(): boolean {
    return this.localUser == null ? false : this.localUser.isAdmin;
  }

  async fetchFirmwareVersions(): Promise<void> {
    try {
      this.firmwares = await firmwareApi.getFirmwareVersions();
    } catch (e) {
      console.error("Failed to load firmware versions", e);
      this.firmwares = [];
    }
  }

  async onStationLocationSave(stationLocation: StationLocation): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.updateStationLocation(
        this.station.uuid,
        stationLocation
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Standort erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Location update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Standort konnte nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async handleImageUpload(file: File): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.updateStationImage(this.station.uuid, file);
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Stationsbild erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Image upload error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Stationsbild konnte nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  @Watch("station")
  async fetchImage(): Promise<void> {
    this.stationImage = null;
    if (!this.station) return;
    try {
      const stationImage = await api.StationsApi.getStationImage(
        this.station.uuid
      );

      this.stationImage = stationImage || null;
    } catch (e) {
      console.warn("No image found for station", this.station.uuid, e);
      this.stationImage = null;
    }
  }

  async onStationPricingSave(pricingModel: Pricing): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.updateStationPricing(
        this.station.uuid,
        pricingModel
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Preise erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Pricing update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Preise konnten nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async onStationTimeZoneSave(timeZone: string): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.updateStationTimeZone(this.station.uuid, timeZone);
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Zeitzone erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Time zone update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Zeitzone konnte nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async onDevicePropertyDescriptionsSave(
    devicePropertyDescriptions: DevicePropertyDescription[]
  ): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.updateDevicePropertyDescriptions(
        this.station.uuid,
        devicePropertyDescriptions
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Geräteeigenschaftsbeschreibungen erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Property descriptions update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message:
          "Geräteeigenschaftsbeschreibungen konnten nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async onDeviceTypeDescriptionsSave(
    deviceTypeDescriptions: DeviceTypeDescription[]
  ): Promise<void> {
    console.log("SAVE")
    if (!this.station) return;
    try {
      await api.StationsApi.updateDeviceTypeDescriptions(
        this.station.uuid,
        deviceTypeDescriptions
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Gerätetypbeschreibungen erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Type descriptions update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Gerätetypbeschreibungen konnten nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async onStationSupportContactsSave(
    stationSupportContacts: StationSupportContacts
  ): Promise<void> {
    if (!this.station) return;

    try {
      await api.StationsApi.updateSupportContacts(
        this.station.uuid,
        stationSupportContacts
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: "Support-Kontakte erfolgreich aktualisiert",
        type: "success",
      });
    } catch (e) {
      console.error("Support contacts update error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Support-Kontakte konnten nicht aktualisiert werden",
        type: "error",
      });
    }
  }

  async onOperatorChooseSave(operatorSummary: OperatorSummary): Promise<void> {
    if (!this.station) return;

    try {
      await api.StationsApi.updateStationOperator(
        this.station.uuid,
        operatorSummary.uuid
      );
      await StationsModule.refreshDetailedStation();
      showToast({
        message: `Betreiber erfolgreich zu ${operatorSummary.name} geändert`,
        type: "success",
      });
    } catch (e) {
      console.error("Operator change error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: "Betreiber konnte nicht geändert werden",
        type: "error",
      });
    }
  }

  async onDeviceHardwareSettingsSave(o: {
    deviceNumber: number;
    deviceHardwareSettings: DeviceHardwareSettings;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceHardwareSettings(
      this.station.uuid,
      o.deviceNumber,
      o.deviceHardwareSettings
    );
    await StationsModule.refreshDetailedStation();
  }

  async onDeviceDescriptionSave(o: {
    deviceNumber: number;
    descriptionText: string;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceDescription(
      this.station.uuid,
      o.deviceNumber,
      o.descriptionText
    );
  }

  async onRentalMethodSave(o: {
    deviceNumber: number;
    rentalMethod: RentalMethod;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceRentalmethod(
      this.station.uuid,
      o.deviceNumber,
      o.rentalMethod
    );
    await StationsModule.refreshDetailedStation();
  }

  async onRentalTimeSave(o: {
    deviceNumber: number;
    maxPrebookDuration: string;
    maxRentalDuration: string;
    minInterBookingDuration: string;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceMaxRentalDuration(
      this.station.uuid,
      o.deviceNumber,
      o.maxRentalDuration
    );
    await api.StationsApi.updateDeviceMaxPrebookDuration(
      this.station.uuid,
      o.deviceNumber,
      o.maxPrebookDuration
    );
    await api.StationsApi.updateMinInterBookingDuration(
      this.station.uuid,
      o.deviceNumber,
      o.minInterBookingDuration
    );
    await StationsModule.refreshDetailedStation();
  }

  async onDeviceDimensionsSave(o: {
    deviceNumber: number;
    deviceDimensions: DeviceDimensions;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceDimensions(
      this.station.uuid,
      o.deviceNumber,
      o.deviceDimensions
    );
    await StationsModule.refreshDetailedStation();
  }

  async onDeviceCapacitySave(o: {
    deviceNumber: number;
    capacity: number;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceCapacity(
      this.station.uuid,
      o.deviceNumber,
      o.capacity
    );

    await StationsModule.refreshDetailedStation();
  }

  async onDevicePropertiesSave(o: {
    deviceNumber: number;
    deviceTypeSettings: DeviceTypeSettings;
  }): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.updateDeviceTypeSettings(
      this.station.uuid,
      o.deviceNumber,
      o.deviceTypeSettings
    );
    await StationsModule.refreshDetailedStation();
  }

  async onDeviceDeleted(deviceNumber: number): Promise<void> {
    if (!this.station) return;
    try {
      await api.StationsApi.deleteDevice(this.station.uuid, deviceNumber);
      await StationsModule.refreshDetailedStation();

      showToast({
        message: `Box ${deviceNumber} erfolgreich gelöscht`,
        type: "success",
      });
    } catch (e) {
      console.error("Device deletion error:", e);
      showToast({
        response: getAxiosResponse(e),
        message: `Box ${deviceNumber} konnte nicht gelöscht werden`,
        type: "error",
      });
    }
  }

  async onDeviceCreated(device: Device): Promise<void> {
    if (!this.station) return;
    await api.StationsApi.createDevice(this.station.uuid, device);
    await StationsModule.refreshDetailedStation();
  }
}

export interface FirmwareVersionOption {
  id: number;
  firmwareId: number;
  label: string;
  version: string;
}
